import { type SupplierChangeReason } from '@utazzo/shared';
import { UzButton, UzDialog, UzRadioGroup, type UzRadioOption } from '@utazzo/ui';
import React, { useState } from 'react';

const REASON_OPTIONS: ReadonlyArray<UzRadioOption<SupplierChangeReason>> = [
  { value: 'MISTAKE', label: 'Added by mistake' },
  { value: 'NOT_SERVICEABLE', label: 'Supplier not serviceable' },
];

interface SupplierChangeReasonDialogProps {
  open: boolean;
  onConfirm: (reason: SupplierChangeReason) => void;
  onCancel: () => void;
}

/**
 * Asks why the supplier on an already-paid line is being changed. The change is
 * sent for admin approval with this reason; cancelling reverts the pick.
 */
export const SupplierChangeReasonDialog = ({
  open,
  onConfirm,
  onCancel,
}: SupplierChangeReasonDialogProps): React.JSX.Element => {
  const [reason, setReason] = useState<SupplierChangeReason | undefined>(undefined);

  return (
    <UzDialog
      open={open}
      onOpenChange={(isOpen) => {
        if (!isOpen) {
          setReason(undefined);
          onCancel();
        }
      }}
      title="Why is the supplier changing?"
      description="This line already has payments. The change will be sent for admin approval."
      size="md"
      footer={
        <div className="flex justify-end gap-2">
          <UzButton
            variant="ghost"
            onClick={() => {
              setReason(undefined);
              onCancel();
            }}
          >
            Cancel
          </UzButton>
          <UzButton
            disabled={!reason}
            onClick={() => {
              if (!reason) return;
              onConfirm(reason);
              setReason(undefined);
            }}
          >
            Confirm
          </UzButton>
        </div>
      }
    >
      <UzRadioGroup<SupplierChangeReason>
        label="Reason"
        required
        options={REASON_OPTIONS}
        value={(reason ?? '') as SupplierChangeReason}
        onValueChange={setReason}
      />
    </UzDialog>
  );
};
